import { useCallback, useEffect, useRef, useState } from "react";
import WaveSurfer, { WaveSurferOptions } from "wavesurfer.js";
import RegionsPlugin from "wavesurfer.js/dist/plugins/regions";
import { convertSecondsToTimeDisplay } from "../../../../helpers/displayHelpers";
import { PlayerTimeState } from "./TimeDisplay";
import { usePlaybackTime } from "../../../../context/PlaybackTimeContext";
import { Duration } from "dayjs/plugin/duration";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { PLAYBACK_SPEED_OPTION } from "../../Video/VideoPlayer";

dayjs.extend(duration);

const DEFAULT_HEIGHT = 200;

const regions = RegionsPlugin.create();

export const waveformConfig = (
  container: HTMLElement,
  height: number | "auto" = DEFAULT_HEIGHT,
): WaveSurferOptions => ({
  container,
  height,
  waveColor: "#163b5b",
  progressColor: "black",
  cursorColor: "black",
  dragToSeek: true,
  autoScroll: true,
  normalize: true,
  fillParent: true,
  plugins: [regions],
  backend: "MediaElement",
});

export interface WaveformProps {
  className?: string;
  height?: number;
  audioPath: string | undefined;
  setIsLoading: (isLoading: boolean) => void;
  isPlaying: boolean;
  setIsPlaying: (isPlaying: boolean) => void;
  volume: number;
  muted: boolean;
  setFailedToLoad: (failedToLoad: boolean) => void;
  onTimeChange: React.Dispatch<
    React.SetStateAction<PlayerTimeState | undefined>
  >;
  redactionTimestamps?: { start: string; end: string }[];
  playbackSpeed: PLAYBACK_SPEED_OPTION;
  mediaElement: React.RefObject<HTMLMediaElement>;
}

const Waveform = ({
  className = "",
  height = DEFAULT_HEIGHT,
  audioPath,
  isPlaying,
  muted,
  volume,
  onTimeChange,
  setIsPlaying,
  setFailedToLoad,
  setIsLoading,
  redactionTimestamps,
  playbackSpeed,
  mediaElement,
}: WaveformProps) => {
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const wavesurfer = useRef<WaveSurfer | null>(null);
  const { setCurrentTimeSeconds, registerSeekHandler } = usePlaybackTime();

  // Register seek handler for this waveform instance
  useEffect(() => {
    const seekHandler = (timeSeconds: number) => {
      if (wavesurfer.current) {
        wavesurfer.current.seekTo(
          timeSeconds / wavesurfer.current.getDuration(),
        );
      }
    };
    const unregister = registerSeekHandler(seekHandler);

    return () => {
      unregister();
    };
  }, [registerSeekHandler]);
  const [isReady, setIsReady] = useState(false);

  const onFinish = useCallback(() => {
    const ws = wavesurfer.current;
    if (!ws) return;
    setIsPlaying(false);
  }, [setIsPlaying]);

  const onReady = useCallback(() => {
    const ws = wavesurfer.current;
    if (!ws) return;

    setIsLoading(false);
    const duration = ws.getDuration();
    onTimeChange?.({
      currentTime: "00:00:00",
      duration: convertSecondsToTimeDisplay(duration),
    });
    setIsPlaying(true);
    setIsReady(true);

    if (!(regions && redactionTimestamps)) {
      return;
    }
    regions.clearRegions();

    const redactions = parseTimestamps(redactionTimestamps);

    for (const redaction of redactions) {
      const startSeconds = redaction.start.asSeconds();
      const endSeconds = redaction.end.asSeconds();

      regions?.addRegion({
        start: startSeconds,
        end: endSeconds,
        color: "rgba(255, 0, 0, 0.3)",
        drag: false,
        resize: false,
      });
    }
  }, [setIsLoading, onTimeChange, setIsPlaying, redactionTimestamps]);

  useEffect(() => {
    if (!waveformRef.current || !audioPath || !mediaElement.current) return;

    setIsReady(false);

    wavesurfer.current = WaveSurfer.create({
      ...waveformConfig(waveformRef.current, height),
      media: mediaElement.current,
      mediaControls: false,
    });
    const ws = wavesurfer.current;
    ws.load(audioPath).catch((error) => {
      console.error("Failed to load audio in WaveSurfer:", error);
      setIsLoading(false);
      setFailedToLoad(true);
    });

    setIsLoading(true);

    ws.on("finish", onFinish);
    ws.on("ready", onReady);

    const updateTime = () => {
      if (!ws) return;
      const currentTimeSeconds = ws.getCurrentTime();
      const currentTime = convertSecondsToTimeDisplay(currentTimeSeconds);
      const duration = convertSecondsToTimeDisplay(ws.getDuration());

      onTimeChange({ currentTime, duration });
      setCurrentTimeSeconds(currentTimeSeconds);
    };

    ws.on("timeupdate", updateTime);

    return () => {
      ws?.un("finish", onFinish);
      ws?.un("timeupdate", updateTime);
      ws?.un("ready", onReady);
      ws?.destroy();
      onTimeChange(undefined);
      setCurrentTimeSeconds(undefined);
    };
  }, [
    audioPath,
    height,
    mediaElement,
    onFinish,
    onTimeChange,
    setIsLoading,
    onReady,
    setCurrentTimeSeconds,
    setFailedToLoad,
  ]);

  useEffect(() => {
    const ws = wavesurfer.current;
    if (!ws || !isReady) return;

    ws.setPlaybackRate(playbackSpeed);
  }, [playbackSpeed, isReady]);

  useEffect(() => {
    const ws = wavesurfer.current;
    if (!ws || !isReady) return;

    if (isPlaying) {
      ws.play();
    } else {
      ws.pause();
    }
  }, [isPlaying, isReady]);

  useEffect(() => {
    if (muted) {
      wavesurfer.current?.setVolume(0);
      return;
    }
    wavesurfer.current?.setVolume(volume / 100);
  }, [muted, volume]);

  return <div className={`${className} cursor-pointer`} ref={waveformRef} />;
};

export default Waveform;

export function parseTimestamps(
  timestamps: { start: string; end: string }[],
): { start: Duration; end: Duration }[] {
  return (
    timestamps?.map(({ start, end }) => {
      return {
        start: durationFromTimeSpanString(start),
        end: durationFromTimeSpanString(end),
      };
    }) ?? []
  );
}

function durationFromTimeSpanString(timeSpan: string): Duration {
  const splits = timeSpan.split(":");
  if (splits.length !== 3) {
    throw new Error(`Invalid time span string: ${timeSpan}`);
  }
  const hours = parseInt(splits[0]);
  const minutes = parseInt(splits[1]);
  const seconds = parseFloat(splits[2]);
  return dayjs.duration({ hours, minutes, seconds });
}
